/**
 * Pure, side-effect-free typing state machine.
 * No React dependency. All operations are O(1).
 *
 * The Set copy on error path is the only allocation per keystroke,
 * and only happens on the error/backspace paths (rare vs. correct typing).
 */

export interface TypingState {
  /** Pre-split character array — NEVER mutated after initialization */
  readonly text: string[];
  /** Current cursor position (next char to be typed) */
  currentIndex: number;
  /** Indices of incorrectly typed characters */
  errors: Set<number>;
}

export type TypingAction =
  | { type: 'KEY_PRESS'; key: string }
  | { type: 'BACKSPACE' }
  | { type: 'RESET' };

export function createInitialTypingState(text: string[]): TypingState {
  return {
    text,
    currentIndex: 0,
    errors: new Set(),
  };
}

export function isComplete(state: TypingState): boolean {
  return state.currentIndex >= state.text.length;
}

function applyKeyPress(state: TypingState, key: string): TypingState {
  if (state.currentIndex >= state.text.length) return state;

  const expected = state.text[state.currentIndex];
  const isCorrect = key === expected;

  // On the happy path (correct key), reuse the same errors reference — no allocation
  let errors = state.errors;
  if (!isCorrect) {
    errors = new Set(state.errors);
    errors.add(state.currentIndex);
  }

  return {
    text: state.text,
    currentIndex: state.currentIndex + 1,
    errors,
  };
}

function applyBackspace(state: TypingState): TypingState {
  if (state.currentIndex === 0) return state;
  const prevIndex = state.currentIndex - 1;

  let errors = state.errors;
  if (errors.has(prevIndex)) {
    errors = new Set(state.errors);
    errors.delete(prevIndex);
  }

  return {
    text: state.text,
    currentIndex: prevIndex,
    errors,
  };
}

export function typingReducer(
  state: TypingState,
  action: TypingAction,
): TypingState {
  switch (action.type) {
    case 'KEY_PRESS':
      return applyKeyPress(state, action.key);
    case 'BACKSPACE':
      return applyBackspace(state);
    case 'RESET':
      return createInitialTypingState(state.text);
    default:
      return state;
  }
}
